import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

/**
 * Hook personalizado para gestionar datos de wallets
 * Incluye fetching, loading states y auto-refresh
 */
export const useWalletData = (userId, autoRefresh = false, refreshInterval = 30000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await api.getWalletsSummary(userId);
      
      setData(result);
      setLastSync(new Date());
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError(err.message || 'Error loading wallet data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch inicial
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchData]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  const syncWallets = useCallback(async () => {
    try {
      setLoading(true);
      await api.syncWallets(userId);
      await fetchData(); // Refresh después de sync
      return true;
    } catch (err) {
      console.error('Error syncing wallets:', err);
      setError(err.message || 'Error syncing wallets');
      return false;
    }
  }, [userId, fetchData]);

  return {
    data,
    loading,
    error,
    lastSync,
    refetch,
    syncWallets,
    
    // Datos específicos con safe access
    totalValueUsd: data?.totalValueUsd || 0,
    totalProfit: data?.totalProfit || 0,
    profitPercentage: data?.profitPercentage || 0,
    binanceSpot: data?.binanceSpot || null,
    binanceFutures: data?.binanceFutures || null,
    solana: data?.solana || null,
  };
};

export default useWalletData;
